import { useRef, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';

// ─────────────────────────────────────────────────────────────
// Champs média réutilisables d'un projet portfolio :
//   • plusieurs images (galerie)
//   • une courte vidéo (optionnelle)
// Utilisé à la création ET à l'édition d'un projet pour garder
// le même comportement partout.
// ─────────────────────────────────────────────────────────────

interface PortfolioGalleryFieldsProps {
  gallery: string[];
  onChangeGallery: (urls: string[]) => void;
  video: string;
  onChangeVideo: (url: string) => void;
  folder?: string;
}

export default function PortfolioGalleryFields({
  gallery,
  onChangeGallery,
  video,
  onChangeVideo,
  folder = 'portfolio',
}: PortfolioGalleryFieldsProps) {
  const imagesInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError(null);
    setUploadingImages(true);
    try {
      const urls: string[] = [];
      for (const file of files) {
        const url = await uploadMediaFile(file, folder);
        if (url) urls.push(url);
      }
      if (urls.length > 0) onChangeGallery([...gallery, ...urls]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'ajout des images");
    } finally {
      setUploadingImages(false);
      if (imagesInputRef.current) imagesInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploadingVideo(true);
    try {
      const url = await uploadMediaFile(file, folder);
      if (url) onChangeVideo(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'ajout de la vidéo");
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const removeImage = (url: string) => onChangeGallery(gallery.filter((u) => u !== url));

  return (
    <div className="space-y-5">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-medium">{error}</div>
      )}

      {/* Galerie d'images */}
      <div>
        <label className="block text-sm font-semibold text-foreground-700 mb-1.5">
          Galerie d&apos;images
        </label>
        <p className="text-xs text-foreground-400 mb-2">
          Ajoutez plusieurs images pour montrer le projet sous tous les angles (PNG, JPG, WEBP — 6 Mo max chacune).
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          {gallery.map((url) => (
            <div key={url} className="relative aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
              <img src={url} alt="Image du projet" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(url)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors"
                title="Retirer cette image"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => imagesInputRef.current?.click()}
            disabled={uploadingImages}
            className="aspect-square rounded-lg border-2 border-dashed border-background-200/70 flex flex-col items-center justify-center gap-1 text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-60"
          >
            {uploadingImages ? (
              <i className="ri-loader-4-line animate-spin text-lg"></i>
            ) : (
              <>
                <i className="ri-add-line text-lg"></i>
                <span className="text-[10px] font-medium">Ajouter</span>
              </>
            )}
          </button>
        </div>
        <input
          ref={imagesInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleImagesUpload}
          className="hidden"
        />
      </div>

      {/* Courte vidéo */}
      <div>
        <label className="block text-sm font-semibold text-foreground-700 mb-1.5">
          Courte vidéo <span className="font-normal text-foreground-400">(optionnelle)</span>
        </label>
        {video ? (
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
            <video src={video} controls className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={() => onChangeVideo('')}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors z-10"
              title="Supprimer la vidéo"
            >
              <i className="ri-delete-bin-line text-xs"></i>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => videoInputRef.current?.click()}
            disabled={uploadingVideo}
            className="w-full h-28 rounded-lg border-2 border-dashed border-background-200/70 flex flex-col items-center justify-center gap-1 text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer bg-background-50 disabled:opacity-60"
          >
            {uploadingVideo ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                <span className="text-xs font-medium text-primary-500">Upload de la vidéo...</span>
              </>
            ) : (
              <>
                <i className="ri-video-add-line text-xl"></i>
                <span className="text-xs font-medium">Ajouter une courte vidéo</span>
                <span className="text-[10px] text-foreground-400">MP4, WEBM — 15 Mo max</span>
              </>
            )}
          </button>
        )}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/webm"
          onChange={handleVideoUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}